import { useEffect, useState } from 'react';
import { getAdminDashboard } from '../api/client';

interface AdminUsageRow {
  public_id: string;
  user_email: string | null;
  plan_name: string;
  tier_name: string;
  monthly_count: number;
  monthly_limit: number | null;
  remaining: number | null;
  reset_at: string;
  is_active: boolean;
}

interface AdminDashboardData {
  users_total: number;
  api_keys_total: number;
  security_last_hour: {
    '401': number;
    '403': number;
    '429': number;
  };
  rows: AdminUsageRow[];
}

export default function Admin() {
  const [dashboard, setDashboard] = useState<AdminDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  const fetchDashboard = async (isManualRefresh = false) => {
    try {
      if (isManualRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError('');
      const response = await getAdminDashboard();
      setDashboard(response.data);
    } catch (err: any) {
      if (err?.response?.status === 403) {
        setError('Admin access required.');
      } else {
        setError('Failed to load admin dashboard.');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchDashboard(false);
  }, []);

  const formatDateTime = (value: string) => {
    if (!value) return 'N/A';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'N/A';
    return date.toLocaleString();
  };

  const getUsagePercent = (count: number, limit: number | null) => {
    if (!limit || limit <= 0) return 0;
    return Math.min(100, Math.max(0, Math.round((count / limit) * 100)));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800">
      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-100">Admin Overview</h1>
            <p className="mt-1 text-base text-slate-300">Global usage, plans, and security signals.</p>
          </div>
          <button
            onClick={() => fetchDashboard(true)}
            disabled={refreshing || loading}
            className="inline-flex items-center justify-center rounded-lg border border-slate-700 bg-slate-900/80 px-4 py-2 text-sm font-medium text-slate-200 hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
          <div className="rounded-xl border border-slate-700 bg-slate-900/80 p-4 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Total Users</p>
            <p className="mt-2 text-3xl font-bold text-slate-100">{dashboard?.users_total ?? 0}</p>
          </div>
          <div className="rounded-xl border border-slate-700 bg-slate-900/80 p-4 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Active API Keys</p>
            <p className="mt-2 text-3xl font-bold text-slate-100">{dashboard?.api_keys_total ?? 0}</p>
          </div>
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-amber-300">401 Last Hour</p>
            <p className="mt-2 text-3xl font-bold text-amber-200">{dashboard?.security_last_hour?.['401'] ?? 0}</p>
          </div>
          <div className="rounded-xl border border-orange-500/30 bg-orange-500/10 p-4 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-orange-300">403 Last Hour</p>
            <p className="mt-2 text-3xl font-bold text-orange-200">{dashboard?.security_last_hour?.['403'] ?? 0}</p>
          </div>
          <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-red-300">429 Last Hour</p>
            <p className="mt-2 text-3xl font-bold text-red-200">{dashboard?.security_last_hour?.['429'] ?? 0}</p>
          </div>
        </div>

        <div className="mt-6 rounded-2xl border border-slate-700 bg-slate-900/80 p-6 shadow-sm">
          <h2 className="text-xl font-semibold text-slate-100">API Usage by Key</h2>
          <p className="mt-1 text-sm text-slate-400">Billing-window usage and quota health.</p>

          {loading ? (
            <div className="py-8 text-slate-400">Loading...</div>
          ) : error ? (
            <div className="mt-4 rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300">
              {error}
            </div>
          ) : !dashboard || dashboard.rows.length === 0 ? (
            <div className="py-8 text-slate-400">No API keys found.</div>
          ) : (
            <div className="mt-5 overflow-x-auto rounded-xl border border-slate-700">
              <table className="min-w-full divide-y divide-slate-700">
                <thead className="bg-slate-900/70">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">
                      User
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">
                      Public ID
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">
                      Tier
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">
                      Plan
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">
                      Usage
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">
                      Monthly Limit
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">
                      Remaining
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">
                      Reset At
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700 bg-slate-950/60">
                  {dashboard.rows.map((row) => (
                    <tr key={row.public_id} className="hover:bg-slate-900/70">
                      <td className="px-4 py-3 text-sm text-slate-300">
                        {row.user_email ?? '—'}
                      </td>
                      <td className="px-4 py-3 text-sm font-mono text-slate-100">
                        {row.public_id}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-300">
                        <span className="inline-flex rounded-full bg-slate-700/60 px-2.5 py-1 text-xs font-semibold text-slate-200">
                          {row.tier_name ?? 'default'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-300">
                        <span className="inline-flex rounded-full bg-indigo-500/20 px-2.5 py-1 text-xs font-semibold uppercase text-indigo-200">
                          {row.plan_name}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-300">
                        <div className="min-w-[160px]">
                          <div className="mb-1 flex items-center justify-between text-xs">
                            <span>{row.monthly_count}</span>
                            <span>{row.monthly_limit ?? 'N/A'}</span>
                          </div>
                          <div className="h-2 w-full overflow-hidden rounded-full bg-slate-700/80">
                            <div
                              className={`h-full rounded-full ${
                                getUsagePercent(row.monthly_count, row.monthly_limit) >= 90
                                  ? 'bg-red-500'
                                  : getUsagePercent(row.monthly_count, row.monthly_limit) >= 70
                                    ? 'bg-amber-500'
                                    : 'bg-emerald-500'
                              }`}
                              style={{ width: `${getUsagePercent(row.monthly_count, row.monthly_limit)}%` }}
                            />
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-300">
                        {row.monthly_limit ?? 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-300">
                        {row.remaining ?? 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-300">
                        {formatDateTime(row.reset_at)}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-300">
                        <span
                          className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                            row.is_active ? 'bg-emerald-500/20 text-emerald-300' : 'bg-slate-700/80 text-slate-300'
                          }`}
                        >
                          {row.is_active ? 'active' : 'inactive'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
